"use client";

import { motion } from "framer-motion";

import PatternBackground from "components/ui/PatternBackground";
import { fadeUpVariant, staggerContainer, cn } from "lib/utils";

export default function PageHero({
  eyebrow,
  title,
  subtitle,
  description,
  backgroundImage,
  children
}) {
  return (
    <section className="relative w-full min-h-[38vh] sm:min-h-[46vh] md:min-h-[52vh] lg:min-h-[58vh] flex items-center justify-center bg-[#24110A] text-singaraa-cream overflow-hidden py-12 sm:py-16 md:py-20">
      {/* Background Image (if provided) */}
      {backgroundImage && (
        <div className="absolute inset-0 z-0">
          <img
            src={backgroundImage}
            alt={title || "Background"}
            className="w-full h-full object-cover opacity-50"
          />
        </div>
      )}

      {/* Gradients and Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#2E160E] via-singaraa-dark to-[#1C0D07] z-10 pointer-events-none" />
      {/* Ambient warm glow in background */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[350px] bg-singaraa-caramel/15 blur-[120px] rounded-full pointer-events-none z-10" />
      <PatternBackground className="opacity-10 z-10 pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-20 relative z-20 flex flex-col items-center justify-center text-center max-w-4xl my-auto">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="flex flex-col items-center justify-center text-center w-full"
        >
          {eyebrow && (
            <motion.span
              variants={fadeUpVariant}
              className="text-singaraa-gold uppercase tracking-[0.2em] sm:tracking-[0.25em] text-[11px] sm:text-xs md:text-sm font-semibold mb-3 md:mb-4 block"
            >
              {eyebrow}
            </motion.span>
          )}

          <motion.h1
            variants={fadeUpVariant}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading text-white leading-[1.15] mb-4 md:mb-6"
          >
            {title}
            {subtitle && (
              <>
                <br />
                <span className="text-singaraa-gold italic font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl">{subtitle}</span>
              </>
            )}
          </motion.h1>

          <motion.p
            variants={fadeUpVariant}
            className={cn(
              "text-sm sm:text-base md:text-lg lg:text-xl text-singaraa-cream/90 font-body max-w-2xl leading-relaxed mx-auto px-2 sm:px-0",
              children ? "mb-6 sm:mb-8" : "mb-0"
            )}
          >
            {description}
          </motion.p>

          {children && (
            <motion.div variants={fadeUpVariant} className="w-full flex justify-center mt-0">
              {children}
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
